import { useCallback, useRef, useState } from 'react';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import pca, { authRequest } from '../../app/msal';
import { env } from '../../../shared/env';

export interface StreamExportProgress {
  step: number;
  totalSteps: number;
  message: string;
}

export interface StreamExportState {
  isLoading: boolean;
  isSuccess: boolean;
  error: string | null;
  progress: StreamExportProgress | null;
}

const initialState: StreamExportState = {
  isLoading: false,
  isSuccess: false,
  error: null,
  progress: null,
};

const acquireAuthToken = async (): Promise<string | undefined> => {
  const settings = {
    ...authRequest,
    account: pca.getAllAccounts()[0] as AccountInfo,
  };
  try {
    const response = await pca.acquireTokenSilent(settings);
    return (response as any)?.idToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await pca.acquireTokenRedirect(settings);
    }
    return undefined;
  }
};

const triggerBrowserDownload = (
  base64Content: string,
  filename: string,
  mimeType: string,
) => {
  const binary = atob(base64Content);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/**
 * Streams CSV export progress events from the backend and triggers a browser
 * download once the final `complete` event arrives. Reads NDJSON over `fetch`
 * (not EventSource) so Bearer auth headers are supported.
 */
export const useStreamExportTasks = () => {
  const [state, setState] = useState<StreamExportState>(initialState);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const start = useCallback(async (csvType: 'tasks' | 'seps') => {
    cancel();
    const controller = new AbortController();
    abortRef.current = controller;
    setState({ ...initialState, isLoading: true });

    try {
      const token = await acquireAuthToken();
      const baseUrl =
        process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'development'
          ? process.env.REACT_APP_API_BASE_URL
          : env.REACT_APP_API_BASE_URL;

      const response = await fetch(
        `${baseUrl}/tasks/export-stream/${csvType}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/x-ndjson',
          },
          signal: controller.signal,
        },
      );

      if (!response.ok || !response.body) {
        throw new Error(`Export request failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let completed = false;

      const handleEvent = (evt: any) => {
        if (evt?.type === 'progress') {
          setState((prev) => ({
            ...prev,
            progress: {
              step: evt.step,
              totalSteps: evt.totalSteps,
              message: evt.message,
            },
          }));
        } else if (evt?.type === 'complete') {
          triggerBrowserDownload(
            evt.content,
            evt.filename || `${csvType}.csv`,
            evt.mimeType || 'text/csv',
          );
          completed = true;
          setState((prev) => ({
            ...prev,
            isLoading: false,
            isSuccess: true,
            progress: {
              step: prev.progress?.totalSteps ?? 1,
              totalSteps: prev.progress?.totalSteps ?? 1,
              message: 'Download ready.',
            },
          }));
        } else if (evt?.type === 'error') {
          throw new Error(evt.message || 'Export failed');
        }
      };

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let newlineIdx = buffer.indexOf('\n');
        while (newlineIdx !== -1) {
          const line = buffer.slice(0, newlineIdx).trim();
          buffer = buffer.slice(newlineIdx + 1);
          if (line) handleEvent(JSON.parse(line));
          newlineIdx = buffer.indexOf('\n');
        }
      }
      const trailing = buffer.trim();
      if (trailing) handleEvent(JSON.parse(trailing));

      if (!completed) {
        throw new Error('Stream ended before completion');
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        setState({ ...initialState });
        return;
      }
      setState({
        isLoading: false,
        isSuccess: false,
        error: err?.message || 'Failed to generate export file.',
        progress: null,
      });
    } finally {
      abortRef.current = null;
    }
  }, [cancel]);

  const reset = useCallback(() => setState(initialState), []);

  return { ...state, start, cancel, reset };
};
